import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloudOff, RefreshCw, CheckCircle2, AlertTriangle, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isOnline, probe, useOnline } from "@/lib/connectivity";
import { device, ldb } from "@/lib/localdb";
import { onSynced, setOutboxBusiness, syncNow, useOutbox } from "@/lib/outbox";
import { blockState, ensureBlock, exhaustBlocks } from "@/lib/receiptBlocks";

// Background sync: outbox upload on reconnect + periodic retry, and keeps a receipt-number block reserved.
export function OfflineEngine() {
  const { session } = useAuth();
  const online = useOnline();
  const bizId = session && !session.impersonation ? session.business.id : null;
  const branchId = session ? session.branch.id : null;
  const live = !!session && !session.offline && !session.impersonation;
  useEffect(() => setOutboxBusiness(bizId), [bizId]);
  useEffect(() => {
    if (!online || !bizId) return;
    syncNow();
    if (live) ensureBlock(branchId);
  }, [online, bizId, branchId, live]);
  useEffect(() => {
    const iv = setInterval(() => isOnline() && syncNow(), 30000);
    const off = onSynced((n) => toast.success("All offline sales synced", { id: "offline-synced", duration: 8000, description: `${n} sale${n === 1 ? "" : "s"} uploaded` }));
    window.__samrtin = Object.assign(window.__samrtin || {}, { ldb, syncNow, probe, ensureBlock, exhaustBlocks, blockState, device });
    return () => {
      clearInterval(iv);
      off();
    };
  }, []);
  return null;
}

function Pill({ tone, icon: Icon, children, state, spin }) {
  const tones = { dark: "bg-slate-900 text-white", ok: "bg-emerald-600 text-white", warn: "bg-amber-100 text-amber-900 ring-1 ring-amber-300", bad: "bg-rose-600 text-white" };
  return (
    <div className={`flex h-9 items-center gap-2 rounded-full px-3.5 text-xs font-semibold shadow-lg ${tones[tone]}`} data-testid="sync-indicator" data-state={state}>
      <Icon className={`h-3.5 w-3.5 shrink-0 ${spin ? "animate-spin" : ""}`} />
      {children}
    </div>
  );
}

export function SyncIndicator() {
  const online = useOnline();
  const ob = useOutbox();
  const { session } = useAuth();
  const [flash, setFlash] = useState(false);
  useEffect(() => onSynced(() => {
    setFlash(true);
    setTimeout(() => setFlash(false), 4000);
  }), []);
  const mine = ob.items.filter((i) => i.branch_id === session.branch.id);
  const pending = mine.filter((i) => i.status === "pending").length;
  const failed = ob.failed;
  let pill = null;
  if (ob.syncing) pill = <Pill tone="dark" icon={RefreshCw} state="syncing" spin>Syncing {ob.syncing} sale{ob.syncing === 1 ? "" : "s"}…</Pill>;
  else if (!online)
    pill = (
      <Pill tone="dark" icon={WifiOff} state="offline">
        Offline — sales are saved on this device
        <span className="ml-1 rounded-full bg-amber-500 px-1.5 font-mono text-[10px] text-slate-950" data-testid="sync-pending-count" title="Sales waiting to sync">{pending} pending</span>
      </Pill>
    );
  else if (flash) pill = <Pill tone="ok" icon={CheckCircle2} state="synced">All offline sales synced</Pill>;
  else if (pending) pill = <Pill tone="warn" icon={CloudOff} state="pending">{pending} sale{pending === 1 ? "" : "s"} waiting to sync</Pill>;
  if (!pill && !failed) return null;
  return (
    <div className="pointer-events-none fixed bottom-24 left-3 z-40 flex flex-col items-start gap-2 md:bottom-4 md:left-4">
      <div className="pointer-events-auto">{pill}</div>
      {failed > 0 && session.user.role === "owner" && (
        <Link to="/sales" className="pointer-events-auto" data-testid="sync-failed-link">
          <Pill tone="bad" icon={AlertTriangle} state="failed">{failed} sale{failed === 1 ? "" : "s"} failed to sync — review</Pill>
        </Link>
      )}
    </div>
  );
}

export function NeedsInternet({ what }) {
  return (
    <div className="grid h-full place-items-center p-8" data-testid="needs-internet">
      <div className="max-w-sm text-center">
        <WifiOff className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">Needs internet</h1>
        <p className="mt-1 text-sm text-slate-500">{what} works when you're back online. The register keeps selling offline.</p>
      </div>
    </div>
  );
}

export function NeedsInternetNote({ className = "" }) {
  return (
    <div className={`flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-600 ${className}`} data-testid="needs-internet-note">
      <WifiOff className="h-4 w-4" /> Needs internet
    </div>
  );
}

export function OnlineOnly({ what, children }) {
  return useOnline() ? children : <NeedsInternet what={what} />;
}
